import {
  CalendarDate,
  getDayOfWeek,
  getLocalTimeZone,
  GregorianCalendar,
  IslamicTabularCalendar,
  PersianCalendar,
  today
} from "@internationalized/date";
import {
  SelectedDate,
  TablePropertiesType
} from "@website/ui/datepicker/components/table/table.types";
import {
  DatepickerDateType,
  DatepickerDayItemType,
  DatepickerDayStateType
} from "@website/ui/datepicker/datepicker.types";
import { convertDate } from "@website/ui/datepicker/datepicker.utils";
import { useSnackbar } from "@website/ui/snackbar";
import {
  DayType,
  gregorianToShamsi,
  MonthType
} from "@website/utils/date-converter";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import omit from "lodash-es/omit";
import { useRouter } from "next/compat/router";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

function isFirstDateOlder(firstDate?: SelectedDate, secondDate?: SelectedDate) {
  if (!firstDate || !secondDate) return false;

  return (
    firstDate?.year < secondDate?.year ||
    (firstDate?.year === secondDate?.year &&
      (firstDate.month < secondDate.month ||
        (firstDate.month === secondDate.month &&
          (firstDate.day || 0) < (secondDate.day || 0))))
  );
}

function isSelectedDateOlder(
  selectedDate?: SelectedDate,
  secondDate?: SelectedDate
) {
  if (!selectedDate || !secondDate) return false;
  return (
    selectedDate.year < secondDate.year ||
    (selectedDate.year === secondDate.year &&
      selectedDate.month <= secondDate.month)
  );
}

function disableUnSelectableDays(
  days: Array<DatepickerDayItemType>,
  properties: TablePropertiesType,
  selectedDateTimestamp: Array<number>,
  onDateClick: (date: DatepickerDateType) => void,
  disableDates?: Array<string>,
  router?: any
) {
  const isOlder = isSelectedDateOlder(
    properties.calendarInfo.selectedDate,
    properties
  );
  const setMinStayDisable = (minStay, startIndex) => {
    if (minStay > 1) {
      for (let index = 1; index <= minStay - 1; index++) {
        const targetIndex = startIndex + index;
        if (targetIndex < days.length && days[targetIndex].value !== -1) {
          days[targetIndex] = {
            ...days[targetIndex],
            state:
              selectedDateTimestamp[0] && selectedDateTimestamp[1]
                ? "in-range"
                : "disabled",
            onClick: undefined
          };
        }
      }
    }
  };
  // If it is older than the month selected by the user, we skip
  if (isOlder) {
    // Check "start" mode is active
    if (selectedDateTimestamp[0] && !selectedDateTimestamp[1]) {
      const findStartIndex = days?.findIndex((item) => item?.state === "start");
      const startIndex = findStartIndex === -1 ? 0 : findStartIndex;
      const minStay = days[startIndex]?.minStay ?? 0;
      setMinStayDisable(minStay, startIndex);
      const limitAfter = convertDate(
        { dateTime: properties.disableLimit?.disableAfterDate },
        properties.calendarLocale
      );
      for (let index = startIndex + (minStay ?? 0); index < 42; index++) {
        const value_ = days[index];
        const newDate = value_?.date;
        const newDateString = newDate?.toString();

        if (value_?.state === "disabled" && value_.value !== -1) {
          if (
            (!properties.disableLimit ||
              isFirstDateOlder(days[index].date, limitAfter) ||
              (days[index].date.year === limitAfter.year &&
                days[index].date.month === limitAfter.month &&
                days[index].date.day === limitAfter.day)) &&
            disableDates?.includes(String(newDate)) &&
            newDateString ===
              disableDates?.find(
                (item) => new Date(item).getTime() > selectedDateTimestamp[0]
              )
          )
            days[index] = {
              ...days[index],
              state: "normal",
              onClick: () => {
                onDateClick({
                  year: newDate.year,
                  month: newDate.month,
                  day: newDate.day,
                  dateTime: newDateString
                });
              }
            };
        } else if (
          disableDates?.includes(
            new Date(selectedDateTimestamp?.[0]).toISOString()?.split("T")?.[0]
          )
        ) {
          return router?.replace(
            {
              query: omit(router?.query, ["date_from", "date_to"])
            },
            undefined,
            { shallow: true, scroll: false }
          );
        }
      }
    }
    if (selectedDateTimestamp[0] && selectedDateTimestamp[1]) {
      const findStartIndex = days?.findIndex((item) => item?.state === "start");
      const startIndex = findStartIndex === -1 ? 0 : findStartIndex;
      const minStay = days[startIndex]?.minStay ?? 0;
      setMinStayDisable(minStay, startIndex);
    }
  }
}

function incrementDate(dateInput, increment = 1) {
  const dateFormatToTime = new Date(dateInput);
  const updateDate = new Date(
    dateFormatToTime.getTime() + increment * 86_400_000
  );
  const year = updateDate.getFullYear();
  const month = String(updateDate.getMonth() + 1).padStart(2, "0");
  const day = String(updateDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const DisableBeforeAndAfterLimit = (
  days: Array<DatepickerDayItemType>,
  properties: TablePropertiesType
) => {
  const { calendarLocale, disableLimit, suggestedDates, value, calendarData } =
    properties;
  const { enqueue } = useSnackbar();

  for (let index = 0; index < 42; index++) {
    const limitAfter = convertDate(
      { dateTime: disableLimit?.disableAfterDate },
      calendarLocale
    );
    const disableBefore = convertDate(
      { dateTime: disableLimit?.disableBeforeDate },
      calendarLocale
    );

    const value_ = days[index];
    if (
      (isFirstDateOlder(limitAfter, value_.date) &&
        disableLimit?.disableAfterDate) ||
      (isFirstDateOlder(value_.date, disableBefore) &&
        disableLimit?.disableBeforeDate)
    ) {
      days[index] = {
        ...days[index],
        state: "disabled",
        onClick: undefined
      };
    }

    if (
      suggestedDates?.length > 0 &&
      suggestedDates.some((item) => {
        const dateFrom = convertDate(
          { dateTime: item?.date_from },
          calendarLocale
        );
        const dateTo = convertDate({ dateTime: item?.date_to }, calendarLocale);
        const dateFromConverted = {
          year: dateFrom.year,
          month: dateFrom.month,
          day: dateFrom.day
        };
        const dateToConverted = {
          year: dateTo.year,
          month: dateTo.month,
          day: dateTo.day
        };

        return (isFirstDateOlder(value?.[0], dateFromConverted) &&
          isFirstDateOlder(dateToConverted, value?.[1])) ||
          (isFirstDateOlder(dateFromConverted, value?.[0]) &&
            isFirstDateOlder(value?.[1], dateToConverted))
          ? false
          : (isFirstDateOlder(dateFromConverted, value_.date) &&
              isFirstDateOlder(value_.date, dateToConverted)) ||
              (isFirstDateOlder(value_.date, dateFromConverted) &&
                isFirstDateOlder(dateToConverted, value_.date));
      })
    ) {
      // eslint-disable-next-line array-callback-return
      suggestedDates.map((item) => {
        let nextDate = incrementDate(item?.date_from);
        while (nextDate !== item.date_to && calendarData[nextDate]?.state) {
          calendarData[nextDate].state = "rack";
          nextDate = incrementDate(nextDate);
        }
      });
      days[index] = {
        ...days[index],
        state: "rack",
        onClick: () => {
          enqueue(
            "این تاریخ فقط در بازه‌های اقامتی مشخصی قابل رزرو می‌باشد.لطفا از بازهایی اقامتی مشخص انتخاب کنید.",
            "default",
            "multi-line"
          );
        }
      };
    }
  }
};

export const useTable = (properties: TablePropertiesType) => {
  const {
    calendarLocale,
    calendarData,
    year,
    month,
    mode,
    value,
    onChange,
    calendarInfo,
    type,
    holidays,
    disableDates
  } = properties;
  const [holidaysList, setHolidaysList] = useState<Array<string>>([]);
  const itemsWithMinStay = Object.values(calendarData || [])?.filter(
    (item) => "minStay" in item
  );
  const router = useRouter();
  useEffect(() => {
    setHolidaysList(
      holidays?.map((date) => {
        const d = gregorianToShamsi(
          Number(date?.slice(0, 4)),
          Number(date?.slice(5, 7)) as MonthType,
          Number(date?.slice(8)) as DayType
        );
        return `${d[0]}${d[1] > 9 ? d[1] : `0${d[1]}`}${
          d[2] > 9 ? d[2] : `0${d[2]}`
        }`;
      }) || []
    );
  }, [holidays]);

  const onDateClick = useCallback(
    (date: DatepickerDateType) => {
      calendarInfo.selectedDate = { year: date?.year, month: date?.month };
      switch (mode) {
        case "single": {
          onChange([date], calendarLocale);
          break;
        }
        case "multiple": {
          const selectedIndex = value.findIndex(
            (item) => item.dateTime === date.dateTime
          );
          if (selectedIndex === -1) {
            onChange([...value, date], calendarLocale);
          } else {
            onChange(
              value.filter((_, index) => index !== selectedIndex),
              calendarLocale
            );
          }
          break;
        }
        case "range":
        case "start-end": {
          if (value[1]) {
            // already has two selected date, reset it
            onChange([date], calendarLocale);
          } else if (value[0]) {
            // start is selected, but end is not
            if (
              new Date(date.dateTime).getTime() >
              new Date(value[0].dateTime).getTime()
            ) {
              onChange([value[0], date], calendarLocale);
            }
            //  When the start date is select twice
            else if (
              new Date(date.dateTime).getTime() ===
              new Date(value[0].dateTime).getTime()
            ) {
              onChange([], calendarLocale);
            } else {
              onChange([date], calendarLocale);
            }
          } else {
            // has no selected date
            onChange([date], calendarLocale);
          }
          break;
        }
        // No default
      }
    },
    [calendarInfo, mode, onChange, calendarLocale, value]
  );

  const disablePastDates = true;

  const todayDate = today(getLocalTimeZone());
  const todayString = todayDate.toString();
  const todayTimestamp = new Date(todayString).getTime();
  const setToolitip = (minStay?: number) => {
    const diffInMs = selectedDateTimestamp[1] - selectedDateTimestamp[0];
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return `${
      minStay
        ? `حداقل اقامت ${numberConvert(minStay, {
            locale: "fa"
          })} شب`
        : diffInDays
          ? itemsWithMinStay?.length > 0
            ? `${numberConvert(diffInDays, {
                locale: "fa"
              })} شب اقامت`
            : ""
          : `حداقل اقامت ${numberConvert(1, {
              locale: "fa"
            })} شب`
    }`;
  };
  const days: Array<DatepickerDayItemType> = [];

  // show date calendar in locale
  let calendar;
  let dayOfWeek;
  let daysInMonth;
  if (calendarLocale === "persian") {
    calendar = new CalendarDate(new PersianCalendar(), year, month, 1);
    daysInMonth = new PersianCalendar().getDaysInMonth(calendar);
    dayOfWeek = getDayOfWeek(calendar, "fa-IR");
  } else if (calendarLocale === "hijri") {
    calendar = new CalendarDate(new IslamicTabularCalendar(), year, month, 1);
    daysInMonth = new IslamicTabularCalendar().getDaysInMonth(calendar);
    dayOfWeek = getDayOfWeek(calendar, "ar-SA");
  } else {
    calendar = new CalendarDate(new GregorianCalendar(), year, month, 1);
    daysInMonth = new GregorianCalendar().getDaysInMonth(calendar);
    dayOfWeek = getDayOfWeek(calendar, "en-US");
    // start of week is sunday.
    // change it to moday.
    dayOfWeek -= 1;
    if (dayOfWeek === -1) {
      dayOfWeek = 6;
    }
  }

  // selected dates timestamp
  const selectedDateTimestamp = useMemo(
    () =>
      value.map((item: DatepickerDateType) =>
        new Date(item.dateTime).getTime()
      ),
    [value]
  );

  for (let index = 0; index < dayOfWeek; index++) {
    days.push({
      value: -1,
      state: "disabled",
      isToday: false,
      onClick: undefined,
      content: undefined
    });
  }
  for (let index = 1; index <= daysInMonth; index++) {
    let state: DatepickerDayStateType = "normal";
    let tooltip = "";
    if ((dayOfWeek + index) % 7 === 0) {
      state = "weekend";
    }
    const newDate = calendar.add({ days: index - 1 });
    const newDateString = newDate.toString();
    const newDateTimestamp = new Date(newDateString).getTime();
    if (
      calendarLocale === "persian" &&
      holidaysList &&
      holidaysList.length > 0 &&
      (dayOfWeek + index) % 7 !== 0
    ) {
      const d = Number(
        `${year}${month > 9 ? month : `0${month}`}${
          index > 9 ? index : `0${index}`
        }`
      );
      for (const date of holidaysList) {
        if (Number(date) === d) {
          state = "holiday";
          break;
        }
        if (Number(date) > d) break;
      }
    }

    let extraData;
    if (calendarData) {
      extraData = calendarData[newDateString];
    }

    if (
      (disablePastDates && newDateTimestamp < todayTimestamp) ||
      extraData?.state === "disabled" ||
      disableDates?.includes(newDateString)
    ) {
      if (
        selectedDateTimestamp[1] &&
        newDateTimestamp === selectedDateTimestamp[1]
      ) {
        state = "end";
        tooltip = setToolitip();
      } else {
        state = "disabled";
      }
    } else if (
      mode === "single" &&
      newDateTimestamp === selectedDateTimestamp[0]
    ) {
      // single
      state = "selected";
    } else if (
      mode === "multiple" &&
      selectedDateTimestamp.includes(newDateTimestamp)
    ) {
      // mutiple
      state = "multi-selected";
    } else if (mode === "range") {
      // range
      if (newDateTimestamp === selectedDateTimestamp[0]) {
        state = "start";
        tooltip =
          selectedDateTimestamp?.length < 2 &&
          setToolitip(calendarData?.[newDateString]?.minStay);
      } else if (
        newDateTimestamp > selectedDateTimestamp[0] &&
        newDateTimestamp < selectedDateTimestamp[1]
      ) {
        state = "in-range";
      } else if (newDateTimestamp === selectedDateTimestamp[1]) {
        state = "end";
        tooltip = setToolitip();
      }
    } else if (mode === "start-end") {
      // start-end
      if (newDateTimestamp === selectedDateTimestamp[0]) {
        state = "start";
      } else if (
        newDateTimestamp > selectedDateTimestamp[0] &&
        newDateTimestamp < selectedDateTimestamp[1]
      ) {
        state = "middle";
      } else if (newDateTimestamp === selectedDateTimestamp[1]) {
        state = "end";
      }
    } else
      switch (extraData?.state) {
        case "green": {
          state = "special-best";

          break;
        }
        case "red": {
          state = "special-worst";

          break;
        }
        case "rack": {
          state = "rack";
          // No default
          break;
        }
      }

    let onClick: undefined | ((event: MouseEvent<HTMLDivElement>) => void);
    if (state !== "disabled" && extraData?.state !== "rack") {
      onClick = () =>
        onDateClick({
          year: newDate.year,
          month: newDate.month,
          day: newDate.day,
          dateTime: newDateString
        });
    }

    days.push({
      value: index,
      state,
      isToday: newDateTimestamp === todayTimestamp,
      onClick,
      content:
        extraData?.content ||
        (extraData?.price
          ? extraData?.availability
            ? numberConvert(
                numberSeparator(
                  (
                    extraData?.price /
                    (process.env.NEXT_PUBLIC_APP_NAME === "villa" ? 1000 : 10)
                  ).toFixed(0)
                ),
                {
                  locale: "fa"
                }
              )
            : "تکمیل"
          : ""),
      date: newDate,
      nonInstantAvailability: extraData?.nonInstantAvailability,
      minStay: extraData?.minStay,
      tooltip,
      availability: extraData?.availability
    });
  }
  for (let index = days.length; index < 42; index++) {
    const newDate = calendar.add({ days: index - 1 });
    days.push({
      value: -1,
      state: "disabled",
      isToday: false,
      onClick: undefined,
      content: undefined,
      date: newDate
    });
  }

  DisableBeforeAndAfterLimit(days, properties);
  if (type === "flat" || itemsWithMinStay.length > 0) {
    disableUnSelectableDays(
      days,
      properties,
      selectedDateTimestamp,
      onDateClick,
      disableDates,
      router
    );
  }

  return {
    days
  };
};
